import React, { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  ExternalLink,
  XCircle,
} from './ui/icons';
import { UrlItem } from '../models/source';
import { UrlActions } from './common/UrlActions';
import { Card, CardContent, Badge, Alert } from './ui';

interface UrlItemCardProps {
  urlItem: UrlItem;
  sourceId: string;
  onUrlAction?: (action: 'process' | 'debug' | 'disable' | 'enable') => void;
}

export function UrlItemCard({
  urlItem,
  sourceId,
  onUrlAction,
}: UrlItemCardProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [actionType, setActionType] = useState<string>('');

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      PENDING: { variant: 'warning' as const, icon: Clock },
      PROCESSING: { variant: 'info' as const, icon: Clock },
      PROCESSED: { variant: 'success' as const, icon: CheckCircle },
      FAILED: { variant: 'error' as const, icon: XCircle },
      FILTERED: { variant: 'warning' as const, icon: AlertTriangle },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] ||
      statusConfig['PENDING'];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} size="sm">
        <Icon className="h-4 w-4 mr-1" />
        {status}
      </Badge>
    );
  };

  const getActiveBadge = (active: boolean) => {
    return (
      <Badge variant={active ? 'success' : 'default'} size="sm">
        {active ? 'Active' : 'Disabled'}
      </Badge>
    );
  };

  const handleTest = async (urlItem: UrlItem) => {
    setIsLoading(true);
    setActionType('debug');
    try {
      if (onUrlAction) {
        await onUrlAction('debug');
      }
    } finally {
      setIsLoading(false);
      setActionType('');
    }
  };

  const handleProcess = async (urlItem: UrlItem) => {
    setIsLoading(true);
    setActionType('process');
    try {
      if (onUrlAction) {
        await onUrlAction('process');
      }
    } finally {
      setIsLoading(false);
      setActionType('');
    }
  };

  const handleToggleActive = async (urlItem: UrlItem) => {
    setIsLoading(true);
    setActionType(urlItem.active ? 'disable' : 'enable');
    try {
      if (onUrlAction) {
        await onUrlAction(urlItem.active ? 'disable' : 'enable');
      }
    } finally {
      setIsLoading(false);
      setActionType('');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Card>
      <CardContent>
        <div className="flex justify-between items-center mb-4">
          <h6 className="text-lg font-medium text-gray-900">Source URL</h6>
          <div className="flex gap-2">
            {getActiveBadge(urlItem.active)}
            {getStatusBadge(urlItem.status)}
          </div>
        </div>

        {/* URL */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-600 mb-2">URL</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 p-2 bg-gray-100 rounded text-sm break-all">
              {urlItem.sourceUrl}
            </code>
            <button
              onClick={() => window.open(urlItem.sourceUrl, '_blank')}
              className="p-2 hover:bg-gray-100 rounded transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Status Information */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm font-medium text-gray-600">Last Processed</p>
            <p className="text-sm text-gray-900">
              {formatDate(urlItem.lastProcessed)}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Created</p>
            <p className="text-sm text-gray-900">
              {formatDate(urlItem.createdAt)}
            </p>
          </div>
        </div>

        {/* Error Message */}
        {urlItem.errorMessage && (
          <Alert variant="error" className="mb-4">
            <strong>Error:</strong> {urlItem.errorMessage}
          </Alert>
        )}

        {/* Filtered Reason */}
        {urlItem.filteredReason && (
          <Alert variant="warning" className="mb-4">
            <strong>Filtered:</strong> {urlItem.filteredReason}
          </Alert>
        )}

        <div className="border-t border-gray-200 my-4" />

        {/* Action Buttons using shared component */}
        <div className="flex justify-center">
          <UrlActions
            urlItem={urlItem}
            sourceId={sourceId}
            onTest={handleTest}
            onProcess={handleProcess}
            onToggleActive={handleToggleActive}
            processing={isLoading && actionType === 'process'}
            showDelete={false}
            size="medium"
            orientation="horizontal"
          />
        </div>
      </CardContent>
    </Card>
  );
}
